import { LazyImageGallery } from "@faire/deprecated-retailer-visitor-shared/components/ImageGallery/index";
import { productMediaFromImages } from "@faire/deprecated-retailer-visitor-shared/components/ImageGallery/utils";
import { MessageBrandCTA } from "@faire/deprecated-retailer-visitor-shared/components/MessageBrandCTA/MessageBrandCTA";
import { useRetailer } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useRetailer";
import { useShouldShowMessageBrandCTA } from "@faire/deprecated-retailer-visitor-shared/lib/messageBrandCTAUtils";
import { useMessengerStoreBrandContext } from "@faire/messenger/store/useMessengerStoreBrandContext";
import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Modal } from "@faire/web/slate/deprecated/Modal";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

interface IProps {
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  onClose: () => void;
}
const noop = () => {};

export const SizeChartModal: React.FC<IProps> = ({
  brand,
  product,
  onClose,
}) => {
  "use no memo"; // mutating hook argument
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
  const [showGallery, setShowGallery] = useState<boolean>(false);
  const { isMobile } = useViewport();
  const { setCurrentBrandContext } = useMessengerStoreBrandContext();
  const { retailer } = useRetailer();

  const shouldShowMessageBrandCTA = useShouldShowMessageBrandCTA(
    brand,
    retailer?.identity_confirmed ?? false
  );

  const [lastBrandToken, setLastBrandToken] = useState<string | undefined>(
    undefined
  );
  useEffect(() => {
    if (brand.token !== lastBrandToken) {
      setCurrentBrandContext(brand);
      setLastBrandToken(brand.token);
    }
    return () => {
      setCurrentBrandContext(undefined);
    };
  }, [brand, setCurrentBrandContext, lastBrandToken]);

  const isSSR = useIsSSR();

  const imageUrl = useMemo(() => {
    const imgSrc = brand.brand_size_chart;

    /**
     * This is specifically to deal with the url being hardcoded in the database
     * for the size chart document. The document api requires an appropriate user
     * session and because this size chart is requested as an image and not an XHR,
     * we cannot intercept this request in our E2E tests. This block will only replace
     * the url hostname if it is localhost. This is less-than-ideal, but a solution
     * to have this feature tested in our E2E tests. This ensures that whatever
     * local forwarding proxy can apply whatever session tokens to the request.
     */
    if (imgSrc?.url && getLocationOrThrow().hostname === "localhost") {
      const imgSrcUrl = new (getWindowOrThrow().URL)(imgSrc.url);
      imgSrc.url = getLocationOrThrow().origin + imgSrcUrl.pathname;
    }
    return imgSrc ? optimizeImageUrl(imgSrc, isSSR) : undefined;
  }, [brand.brand_size_chart, isSSR]);

  useEffect(() => {
    // Preloading the size chart to prevent jumping around when it loads in
    // doing this because we are fitting the modal to the image
    if (isImageLoading) {
      const window = getWindowOrThrow();
      const image = new window.Image();
      image.onload = () => setIsImageLoading(false);
      image.src = imageUrl as string;
    }
  }, [imageUrl, isImageLoading]);

  if (isImageLoading) {
    return null;
  }

  if (showGallery && isMobile && brand.brand_size_chart) {
    return (
      <Suspense fallback={null}>
        <LazyImageGallery
          index={0}
          productMedia={productMediaFromImages([brand.brand_size_chart])}
          onChangeIndex={noop}
          onClose={() => setShowGallery(false)}
          eventName={`size-chart/${brand?.token}/${product.token}`}
        />
      </Suspense>
    );
  }

  return (
    <ModalStyled
      isOpen
      onClose={onClose}
      width="auto"
      height="auto"
      padding="30px"
      data-test-id="size-chart-modal"
    >
      <Container>
        <Typography variant="sectionHeaderSansMedium">
          <StrictLocalMsg
            id="nYZHWH"
            defaultMessage="{brandName} size chart"
            description={{
              text: "the clothing size chart for a specific brand",
            }}
            values={DEPRECATED_backwardsCompatibleValues({
              brandName: brand?.name,
            })}
          />
        </Typography>

        {shouldShowMessageBrandCTA ? (
          <>
            <Spacer size={4} />
            <Row>
              <Typography>
                <StrictLocalMsg
                  id="sHGa15"
                  defaultMessage="Questions about sizing?"
                  description={{
                    text: "Button to contact brand with any questions related to the size of a product",
                  }}
                />
              </Typography>
              <Spacer size={4} />
              <MessageBrandCTA brand={brand} />
            </Row>
          </>
        ) : null}

        <Spacer size={24} />
        <StyledImage
          src={imageUrl}
          onClick={() => setShowGallery(true)}
          data-test-id="size-chart-modal-image"
        />
        {isImageLoading ? (
          <ImagePlaceholder>
            <LoadingSpinner size="xLarge" />
          </ImagePlaceholder>
        ) : null}
      </Container>
    </ModalStyled>
  );
};

const ModalStyled = styled(Modal)`
  ${responsiveMediaQueries.tabletAndAbove} {
    min-height: 600px;
    max-height: 80vh;
  }
`;

const Container = styled(Column)`
  position: relative;
  ${responsiveMediaQueries.tabletAndAbove} {
    max-height: calc(80vh - 60px);
  }
`;

const StyledImage = styled.img`
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    min-width: 500px;
    max-width: 80vw;
  }
`;

const ImagePlaceholder = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  height: calc(100vh - 132px);
  max-height: 468px;
`;
